import axios from 'axios';
import config from '../config/environment';

export const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'cookie';

axios.defaults.baseURL = config.apiGatewayUrl;
axios.defaults.headers.common['Content-Type'] = 'application/json';

if (AUTH_MODE === 'cookie') {
  axios.defaults.withCredentials = true;
}

// Attach Bearer token on every request (header mode)
axios.interceptors.request.use(
  (reqConfig) => {
    // Skip auto-attaching token if the request explicitly opted out (used for payout token requests)
    if (AUTH_MODE === 'header' && !reqConfig._skipAuthInterceptor) {
      const token = localStorage.getItem('token');
      if (token) reqConfig.headers.Authorization = 'Bearer ' + token;
    }
    return reqConfig;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue  = [];

const processQueue = (error, token) => {
  failedQueue.forEach(p => error ? p.reject(error) : p.resolve(token));
  failedQueue = [];
};

const redirectToLogin = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userData');
  const path = window.location.pathname;
  if (path.includes('/login') || path.includes('/signup') || path.startsWith('/payment/')) return;
  try {
    const role = JSON.parse(localStorage.getItem('userData') || '{}').role;
    const isAdmin = role === 'ADMIN' || path.startsWith('/admin') || path.startsWith('/secure-admin');
    window.location.href = isAdmin ? '/secure-admin-portal/login' : '/login';
  } catch {
    window.location.href = '/login';
  }
};

// Silent token refresh on 401:
// - Access token expires every 15 min
// - Interceptor auto-refreshes using the 24hr refresh token
// - User stays logged in until refresh token itself expires
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const orig   = error.config;
    const status = error.response?.status;
    const isAuth = orig?.url?.includes('/api/auth/');

if (status !== 401 || orig._retry || isAuth) {
  return Promise.reject(error);
}

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      redirectToLogin();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (AUTH_MODE === 'header') orig.headers.Authorization = 'Bearer ' + token;
        return axios(orig);
      }).catch((err) => Promise.reject(err));
    }

    orig._retry   = true;
    isRefreshing  = true;

    try {
      const res = await axios.post('/api/auth/refresh?refreshToken=' + encodeURIComponent(refreshToken));

      if (res.data && res.data.success) {
        const newToken   = res.data.data.token;
        const newRefresh = res.data.data.refreshToken;

        if (AUTH_MODE === 'header') {
          if (newToken)   localStorage.setItem('token', newToken);
          if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
          orig.headers.Authorization = 'Bearer ' + newToken;
        }

        processQueue(null, newToken);
        return axios(orig);
      }

      processQueue(new Error('refresh failed'), null);
      redirectToLogin();
      return Promise.reject(error);

    } catch (refreshErr) {
      processQueue(refreshErr, null);
      const s = refreshErr.response?.status;
      // Only force logout on 4xx (invalid/expired refresh token)
      // 5xx or network errors are transient — don't kick the user out
      if (s && s >= 400 && s < 500) {
        redirectToLogin();
      }
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

const api = axios;
export default api;
